// src/pages/AuthPage.js
import { useState, useEffect } from "react";
import "./AuthPage.css";
import logo from "../assets/images/logo.png";
import axios from "axios";
import { API } from "../services/api"; // adjust path if needed

const AuthPage = () => {
    const [activeTab, setActiveTab] = useState("login");
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
    });

    const getErrMsg = (err, fallback = "Something went wrong") =>
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        fallback;

    const safePayload = (payload) => {
        // don't leak password to the console
        if (!payload) return payload;
        const copy = { ...payload };
        if (copy.password) copy.password = "***masked***";
        return copy;
        // If payload is a primitive (e.g., Google token), just return it
    };

    // 🔐 Google Login
    const handleGoogleResponse = async (response) => {
        const token = response.credential;
        setLoading(true);

        const url = API.googleLogin;
        const payload = { token };

        console.groupCollapsed("[AUTH] Google Login → Request");
        console.log("URL:", url);
        console.log("Payload:", safePayload(payload));
        console.groupEnd();

        try {
            const { data, status, headers } = await axios.post(url, payload, {
                headers: { "Content-Type": "application/json" },
                // withCredentials: false // not needed, but you can force it if you want
            });

            console.groupCollapsed("[AUTH] Google Login ← Response");
            console.log("Status:", status);
            console.log("Headers:", headers);
            console.log("Data:", data);
            console.groupEnd();

            localStorage.setItem("token", data.token);
            localStorage.setItem("user", JSON.stringify(data.user));
            window.location.href = "/";
        } catch (err) {
            console.groupCollapsed("[AUTH] Google Login ✖ Error");
            console.log("URL:", url);
            console.log("Payload:", safePayload(payload));
            console.log("Status:", err?.response?.status);
            console.log("Headers:", err?.response?.headers);
            console.log("Data:", err?.response?.data);
            console.log("Error:", err);
            console.groupEnd();

            alert("Google login failed: " + getErrMsg(err, "Unknown error"));
        } finally {
            setLoading(false);
        }
    };

    // 🔐 Manual Login/Signup
    const handleManualSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const url = activeTab === "login" ? API.login : API.signup;
        const payload = { ...formData };

        console.groupCollapsed("[AUTH] Manual Auth → Request");
        console.log("URL:", url);
        console.log("Payload:", safePayload(payload));
        console.groupEnd();

        try {
            const { data, status, headers } = await axios.post(url, payload, {
                headers: { "Content-Type": "application/json" },
            });

            console.groupCollapsed("[AUTH] Manual Auth ← Response");
            console.log("Status:", status);
            console.log("Headers:", headers);
            console.log("Data:", data);
            console.groupEnd();

            localStorage.setItem("token", data.token);
            localStorage.setItem("user", JSON.stringify(data.user));
            window.location.href = "/";
        } catch (err) {
            console.groupCollapsed("[AUTH] Manual Auth ✖ Error");
            console.log("URL:", url);
            console.log("Payload:", safePayload(payload));
            console.log("Status:", err?.response?.status);
            console.log("Headers:", err?.response?.headers);
            console.log("Data:", err?.response?.data);
            console.log("Error:", err);
            console.groupEnd();

            alert(getErrMsg(err, "Authentication failed. Please try again."));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    // 👇 Google Sign-In Initialization
    useEffect(() => {
        const div = document.getElementById("googleSignInDiv");
        if (window.google && div) {
            window.google.accounts.id.initialize({
                client_id:
                    "1097509269540-fed47vj5gjkhn6qnvom57smdks5hlrve.apps.googleusercontent.com",
                callback: handleGoogleResponse,
            });

            window.google.accounts.id.renderButton(div, {
                theme: "filled_blue",
                size: "large",
                shape: "pill",
                text: "continue_with",
                width: 800,
                logo_alignment: "left",
            });
        }
    }, [activeTab]);

    return (
        <div className="auth-wrapper d-flex justify-content-center align-items-center">
            <div className="auth-box shadow p-4 bg-white rounded">
                <div className="text-center mb-4">
                    <img src={logo} alt="Logo" className="logo-img" />
                    <h2 className="brand-title">AVNII</h2>
                    <p className="text-muted">
                        {activeTab === "login"
                            ? "Login to your account"
                            : "Create a new account"}
                    </p>
                </div>

                <div className="btn-group d-flex mb-4" role="group">
                    <button
                        className={`btn ${activeTab === "login" ? "btn-gold" : "btn-outline-gold"
                            }`}
                        onClick={() => setActiveTab("login")}
                    >
                        Login
                    </button>
                    <button
                        className={`btn ${activeTab === "signup" ? "btn-gold" : "btn-outline-gold"
                            }`}
                        onClick={() => setActiveTab("signup")}
                    >
                        Sign Up
                    </button>
                </div>

                <form onSubmit={handleManualSubmit}>
                    {activeTab === "signup" && (
                        <div className="mb-3">
                            <input
                                type="text"
                                name="name"
                                className="form-control"
                                placeholder="Full Name"
                                value={formData.name}
                                onChange={handleChange}
                                autoComplete="name"
                                required
                            />
                        </div>
                    )}

                    <div className="mb-3">
                        <input
                            type="email"
                            name="email"
                            className="form-control"
                            placeholder="Email address"
                            value={formData.email}
                            onChange={handleChange}
                            autoComplete="email"
                            required
                        />
                    </div>

                    <div className="mb-3">
                        <input
                            type="password"
                            name="password"
                            className="form-control"
                            placeholder="Password"
                            value={formData.password}
                            onChange={handleChange}
                            autoComplete="current-password"
                            required
                        />
                    </div>

                    <button type="submit" className="btn btn-gold w-100" disabled={loading}>
                        {loading
                            ? activeTab === "login"
                                ? "Logging in..."
                                : "Creating Account..."
                            : activeTab === "login"
                                ? "Login"
                                : "Create Account"}
                    </button>

                    <div className="divider my-3">or</div>
                    <div id="googleSignInDiv" className="mb-3 google-btn-wrapper"></div>
                </form>
            </div>
        </div>
    );
};

export default AuthPage;
